import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  TILE_SIZE,
  loadModel,
  setupTrackSceneAtmosphere,
  STRAIGHT_EXTRA_ROTATION,
  type TrackSceneBounds,
} from '../circuits/circuit3dTrackScene.js';
import { buildProceduralStraight } from '../circuits/proceduralRoadPieces.js';
import { createPillButtonGroup } from '../ui/pillButtonGroup.js';
import { createMultirxShineLoader } from '../ui/multirxShineLoader.js';
import { normalizeCarMaterials } from './car3dMaterial.js';
import {
  cacheBustCarGlbUrl,
  computeCarOrientationFix,
  facingRotationY,
} from './carOrientationFix.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import { rotateCarModel3d, type Car3dModelItem } from './api.js';

export type CarOrientationEmbedHandle = {
  dispose: () => void;
  reload: () => Promise<void>;
  /** Résolu une fois le premier chargement GLB terminé (`true` = ok). */
  ready: Promise<boolean>;
};

const ROTATION_STEPS: ReadonlyArray<90 | 180 | 270> = [90, 180, 270];
const GHOST_OPACITY = 0.35;
/** Au-delà, on considère le chargement planté (slot WebGL bloqué sinon). */
export const ORIENTATION_EMBED_LOAD_TIMEOUT_MS = 12_000;

const ATMOSPHERE_BOUNDS: TrackSceneBounds = {
  minX: -3,
  minY: -3,
  maxX: 4,
  maxY: 4,
  centerX: 0.5,
  centerZ: 0.5,
  span: 6,
};

function disposeObject3D(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose();
      const material = child.material;
      for (const m of Array.isArray(material) ? material : [material]) {
        if (m instanceof THREE.MeshStandardMaterial) m.map?.dispose();
        m.dispose();
      }
    }
  });
}

function setOpacity(obj: THREE.Object3D, opacity: number): void {
  obj.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    for (const m of materials) {
      m.transparent = true;
      m.opacity = opacity;
      m.depthWrite = false;
    }
  });
}

function cloneWithOwnMaterials(obj: THREE.Object3D): THREE.Object3D {
  const clone = obj.clone(true);
  clone.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.material = Array.isArray(child.material)
        ? child.material.map((m) => m.clone())
        : child.material.clone();
    }
  });
  return clone;
}

/**
 * Mini outil de recadrage inline (piste + flèche + rotations 90/180/270°)
 * pour une carte du catalogue garage.
 */
export function mountCarOrientationEmbed(
  host: HTMLElement,
  item: Car3dModelItem
): CarOrientationEmbedHandle {
  host.classList.add('car-orientation-embed');
  host.replaceChildren();

  const viewport = document.createElement('div');
  viewport.className = 'car-orientation-embed__viewport';

  const actionsEl = document.createElement('div');
  actionsEl.className = 'car-orientation-embed__actions';

  const statusEl = document.createElement('p');
  statusEl.className = 'car-orientation-embed__status';
  statusEl.setAttribute('role', 'status');

  host.append(viewport, actionsEl, statusEl);

  const loader = createMultirxShineLoader('Chargement du modèle…');
  viewport.appendChild(loader);

  let scene: THREE.Scene | null = null;
  let camera: THREE.PerspectiveCamera | null = null;
  let renderer: THREE.WebGLRenderer | null = null;
  let controls: OrbitControls | null = null;
  let frameId = 0;
  let resizeObserver: ResizeObserver | null = null;
  let sceneDisposed = false;
  let currentModel: THREE.Object3D | null = null;
  let ghostModel: THREE.Object3D | null = null;
  let loadGeneration = 0;
  let rotateDisabled = false;
  const rotateButtons: HTMLElement[] = [];
  let settleReady: ((ok: boolean) => void) | null = null;
  const ready = new Promise<boolean>((resolve) => {
    settleReady = resolve;
  });

  function setStatus(message: string, isError = false): void {
    statusEl.textContent = message;
    statusEl.classList.toggle('car-orientation-embed__status--error', isError);
  }

  function setLoaderVisible(visible: boolean): void {
    loader.hidden = !visible;
  }

  function finishReady(ok: boolean): void {
    if (!settleReady) return;
    settleReady(ok);
    settleReady = null;
  }

  function placeCarModel(model: THREE.Object3D, slug: string, extraDeltaDeg: number): void {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const scale = (TILE_SIZE * 0.5) / Math.max(size.x, size.z, 0.001);
    model.scale.setScalar(scale);
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = false;
        child.receiveShadow = false;
      }
    });
    normalizeCarMaterials(model);
    const groundedBox = new THREE.Box3().setFromObject(model);
    const groundY = -groundedBox.min.y + 0.02;
    const orientationFix = computeCarOrientationFix(slug);
    model.position.set(0.5, groundY, 0.5);
    model.rotation.y = facingRotationY(orientationFix, 0) + (extraDeltaDeg * Math.PI) / 180;
  }

  function clearGhost(): void {
    if (!ghostModel || !scene) return;
    scene.remove(ghostModel);
    disposeObject3D(ghostModel);
    ghostModel = null;
  }

  function showGhost(deltaDeg: number): void {
    if (!currentModel || !scene) return;
    clearGhost();
    const clone = cloneWithOwnMaterials(currentModel);
    clone.rotation.y += (deltaDeg * Math.PI) / 180;
    setOpacity(clone, GHOST_OPACITY);
    ghostModel = clone;
    scene.add(ghostModel);
  }

  function setRotateButtonsDisabled(disabled: boolean): void {
    rotateDisabled = disabled;
    for (const btn of rotateButtons) {
      if (disabled) btn.setAttribute('disabled', 'true');
      else btn.removeAttribute('disabled');
    }
  }

  async function loadAndShowCar(): Promise<void> {
    if (!scene) return;
    clearGhost();
    if (currentModel) {
      scene.remove(currentModel);
      disposeObject3D(currentModel);
      currentModel = null;
    }
    setLoaderVisible(true);
    setStatus(`Chargement de « ${item.slug} »…`);
    const myGeneration = ++loadGeneration;
    let timeoutId = 0;
    try {
      const model = await Promise.race([
        loadModel(cacheBustCarGlbUrl(item.publicPath)),
        new Promise<never>((_, reject) => {
          timeoutId = window.setTimeout(() => {
            reject(new Error('timeout'));
          }, ORIENTATION_EMBED_LOAD_TIMEOUT_MS);
        }),
      ]);
      window.clearTimeout(timeoutId);
      if (myGeneration !== loadGeneration || !scene || sceneDisposed) return;
      placeCarModel(model, item.slug, 0);
      currentModel = model;
      scene.add(currentModel);
      setLoaderVisible(false);
      setStatus(`Modèle : ${item.slug}`);
      finishReady(true);
    } catch {
      window.clearTimeout(timeoutId);
      if (myGeneration !== loadGeneration) return;
      setLoaderVisible(false);
      setStatus(`Impossible de charger « ${item.slug} ».`, true);
      finishReady(false);
    }
  }

  async function commitRotation(degrees: 90 | 180 | 270): Promise<void> {
    if (rotateDisabled) return;
    setRotateButtonsDisabled(true);
    clearGhost();
    setStatus(`Rotation de ${degrees}°…`);
    try {
      await rotateCarModel3d(item.slug, degrees);
      await loadAndShowCar();
      setStatus(`« ${item.slug} » tourné de ${degrees}° — enregistré.`);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Erreur lors de la rotation.', true);
    } finally {
      setRotateButtonsDisabled(false);
    }
  }

  // Scène — zoom ×2 vs vue initiale (moitié du zoom trop serré précédent)
  const width = viewport.clientWidth || 320;
  const height = viewport.clientHeight || 200;
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(45, width / Math.max(height, 1), 0.1, 100);
  camera.position.set(0.5 + 0.175, 1.125, 0.5 + 0.85);

  // preserveDrawingBuffer : capture PNG fiable avant libération du contexte.
  renderer = new THREE.WebGLRenderer({
    antialias: false,
    powerPreference: 'low-power',
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.setSize(width, height);
  viewport.appendChild(renderer.domElement);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(0.5, 0.15, 0.5);
  controls.maxPolarAngle = Math.PI * 0.49;
  controls.minDistance = 0.4;
  controls.maxDistance = 8;
  controls.mouseButtons.RIGHT = null;
  controls.update();

  setupTrackSceneAtmosphere(scene, ATMOSPHERE_BOUNDS);
  const roadGroup = new THREE.Group();
  roadGroup.position.set(0.5, 0, 0.5);
  scene.add(roadGroup);
  const road = buildProceduralStraight({ curbColor: 0xffffff });
  road.rotation.y = STRAIGHT_EXTRA_ROTATION;
  roadGroup.add(road);
  scene.add(
    new THREE.ArrowHelper(
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(0.15, 0.05, 0.82),
      0.55,
      0xffd400,
      0.16,
      0.09
    )
  );

  function animate(): void {
    if (sceneDisposed || !renderer || !scene || !camera) return;
    controls?.update();
    renderer.render(scene, camera);
    frameId = requestAnimationFrame(animate);
  }
  frameId = requestAnimationFrame(animate);

  resizeObserver = new ResizeObserver(() => {
    if (!camera || !renderer) return;
    const w = viewport.clientWidth || width;
    const h = viewport.clientHeight || height;
    camera.aspect = w / Math.max(h, 1);
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  });
  resizeObserver.observe(viewport);

  const rotateGroup = createPillButtonGroup(
    ROTATION_STEPS.map((degrees) => ({
      label: `↻ ${degrees}°`,
      variant: 'soft' as const,
      onClick: () => void commitRotation(degrees),
    })),
    { small: true, ariaLabel: 'Rotation du modèle' }
  );
  for (const [i, degrees] of ROTATION_STEPS.entries()) {
    const btn = rotateGroup.children[i];
    if (!(btn instanceof HTMLElement)) continue;
    btn.dataset.role = 'admin,moderator';
    btn.addEventListener('mouseenter', () => showGhost(degrees));
    btn.addEventListener('mouseleave', () => clearGhost());
    btn.addEventListener('focus', () => showGhost(degrees));
    btn.addEventListener('blur', () => clearGhost());
    rotateButtons.push(btn);
  }
  actionsEl.appendChild(rotateGroup);
  applyRoleVisibility();

  void loadAndShowCar();

  return {
    reload: loadAndShowCar,
    ready,
    dispose() {
      sceneDisposed = true;
      loadGeneration += 1;
      finishReady(false);
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      resizeObserver = null;
      controls?.dispose();
      controls = null;
      clearGhost();
      if (currentModel) disposeObject3D(currentModel);
      currentModel = null;
      if (renderer) {
        try {
          renderer.forceContextLoss();
        } catch {
          /* ignore */
        }
        renderer.dispose();
        renderer.domElement.remove();
        renderer = null;
      }
      scene = null;
      camera = null;
      host.replaceChildren();
      host.classList.remove('car-orientation-embed');
    },
  };
}
